import type { Page, Signal, SignalPromoteResult } from '@fruuit/shared'
import { api } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'

export interface SignalFilters {
  provider: string
  severity: string
  status: string
  q: string
}

export const signalKeys = {
  all: ['signals'] as const,
  list: (filters: SignalFilters, cursor: string | null) =>
    ['signals', 'list', filters, cursor] as const,
}

/** `GET /signals`: triage inbox, newest first. Empty filters are dropped from the query. */
export function useSignals(filters: SignalFilters, cursor: string | null) {
  return useApiQuery<Page<Signal>>(signalKeys.list(filters, cursor), '/signals', {
    query: {
      provider: filters.provider || undefined,
      severity: filters.severity || undefined,
      status: filters.status || undefined,
      q: filters.q.trim() || undefined,
      limit: 25,
      cursor: cursor ?? undefined,
    },
    placeholderData: (previous) => previous,
  })
}

const invalidate = [signalKeys.all, ['admin', 'overview']]

export function useDismissSignal() {
  return useApiMutation(
    (vars: { id: string; revision: number; reason: string }, { requestId }) =>
      api.post<Signal>(
        `/signals/${encodeURIComponent(vars.id)}/dismiss`,
        { revision: vars.revision, reason: vars.reason },
        { requestId },
      ),
    { invalidate },
  )
}

export function usePromoteSignal() {
  return useApiMutation(
    (vars: { id: string; revision: number; note?: string }, { requestId }) =>
      api.post<SignalPromoteResult>(
        `/signals/${encodeURIComponent(vars.id)}/promote`,
        vars.note ? { revision: vars.revision, note: vars.note } : { revision: vars.revision },
        { requestId },
      ),
    { invalidate },
  )
}
